<script setup lang="ts">
import {
    ToastProvider,
    ToastRoot,
    ToastTitle,
    ToastViewport,
    ToastClose
} from "reka-ui";
import { useNotificationStore, type NotificationType } from "@/shared/composables/useNotificationStore";
import { cn } from "@/lib/utils";

const { notifications, removeNotification } = useNotificationStore();

const typeClasses: Record<NotificationType, { container: string; icon: string }> = {
    success: {
        container: "bg-teal-50 border-teal-200 text-teal-800",
        icon: '<path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path><path d="m9 12 2 2 4-4"></path>'
    },
    warning: {
        container: "bg-yellow-50 border-yellow-200 text-yellow-800",
        icon: '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path>'
    },
    error: {
        container: "bg-red-50 border-red-200 text-red-800",
        icon: '<circle cx="12" cy="12" r="10"></circle><path d="m15 9-6 6"></path><path d="m9 9 6 6"></path>'
    }
};
</script>

<template>
    <ToastProvider>
        <ToastRoot
            v-for="notification in notifications"
            :key="notification.id"
            :duration="0"
            :open="true"
            :class="cn(
                'border rounded-lg p-4 text-sm pointer-events-auto',
                'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
                typeClasses[notification.type].container
            )"
            role="alert"
            data-testid="notification-toast"
            @update:open="(open: boolean) => { if (!open) removeNotification(notification.id); }"
        >
            <div class="flex items-start gap-3">
                <div class="shrink-0">
                    <svg
                        class="shrink-0 size-4 mt-0.5"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        v-html="typeClasses[notification.type].icon"
                    />
                </div>
                <ToastTitle class="text-sm font-semibold flex-1">
                    {{ notification.message }}
                </ToastTitle>
                <ToastClose
                    class="shrink-0 rounded-md p-0.5 opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
                    :aria-label="'Close'"
                    @click="removeNotification(notification.id)"
                >
                    <svg
                        class="size-4"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                </ToastClose>
            </div>
        </ToastRoot>

        <ToastViewport
            class="fixed z-50 top-6 right-6 flex flex-col gap-2 w-96 max-w-[calc(100vw-3rem)] pointer-events-none"
            data-testid="notification-viewport"
        />
    </ToastProvider>
</template>
